// @givmo/employer-module — the module's build-time switches, READ FROM ITS HOST (EP-MNT3).
//
// A package never reads `import.meta.env`: the env is the HOST's build, not the package's (the rule
// `@givmo/api-client`, `@givmo/analytics` and `@givmo/observability` already keep). So the host hands
// its env over ONCE, at boot, before anything renders:
//
//     configureEmployerModuleEnv(import.meta.env);   // apps/admin/src/main.tsx, preview, test setup
//
// and every reader below defaults to that source. Each still takes an explicit `source` for tests,
// exactly as the readers did in `apps/admin/src/lib/env.ts` before the move — that file now
// re-exports these five, so the portal's shell, routes and pages read ONE implementation.
//
// ⚠ An UNCONFIGURED module reads every switch as OFF (an empty env). That is the fail-closed answer
// for a dark surface, and the wrong one for a switch that is on in prod — so the admin app pins that
// both of its entries configure before `createRoot` (`apps/admin/src/lib/employer-module-env.test.ts`).
//
// Each switch keeps its original semantics byte for byte: on iff the variable, trimmed and
// lower-cased, is exactly `'true'`.

/** The env a host hands over — its `import.meta.env`, or a plain object in a test. */
export type EmployerModuleEnv = { readonly [name: string]: unknown };

let hostEnv: EmployerModuleEnv = {};

/** Hand the module its host's env. Call once, at boot, before the first render. */
export function configureEmployerModuleEnv(source: EmployerModuleEnv): void {
  hostEnv = source;
}

function isOn(value: unknown): boolean {
  return typeof value === 'string' && value.trim().toLowerCase() === 'true';
}

/**
 * EP-WEB1 dark switch — the company DIRECT-GIVING flow (a company gifting its OWN money from the
 * portal, independent of matching). DEFAULT OFF in every environment: the flow is UNREACHABLE while
 * off (no dashboard entry point renders, and the `/give` route redirects to `/dashboard`), so an
 * admin never sees a surface that would 400 at the backend funding gate.
 *
 * Activation is a TWO-SIDED flip in one promotion round (EP-ACT1): set this build-time var
 * `VITE_COMPANY_DIRECT_GIVING_ENABLED=true` for the env's admin build AND flip the backend
 * `COMPANY_FUNDING_SOURCE_ROUTING_ENABLED` env var for the same env together — a visible flow against
 * an OFF backend would be rejected at `_resolve_company_funded_company` (CAM-BE4). Read from the env
 * the host handed over (Vite injects `VITE_*` into it at build time); `source` is injectable for tests.
 */
export function isCompanyDirectGivingEnabled(source: EmployerModuleEnv = hostEnv): boolean {
  return isOn(source.VITE_COMPANY_DIRECT_GIVING_ENABLED);
}

/**
 * EP-WEB4 dark switch — the company GIFT CARDS area (employer gifting v1: a company sending a
 * company-funded gift card to a roster employee, via `POST /admin/company/gift-cards`). DEFAULT OFF
 * in every environment: while off the whole area is UNREACHABLE (no sidebar entry renders, and the
 * `/gift-cards` route redirects to `/dashboard`), so an admin never sees a surface whose send/list
 * endpoints 404 at the backend.
 *
 * Activation is a TWO-SIDED flip in one promotion round: set this build-time var
 * `VITE_COMPANY_GIFT_CARDS_ENABLED=true` for the env's admin build AND flip the backend
 * `COMPANY_GIFT_CARDS_ENABLED` env var for the same env together — a visible area against an OFF
 * backend would 404 at the two `/admin/company/gift-cards` routes. Read from the env the host handed
 * over (Vite injects `VITE_*` into it at build time); `source` is injectable for tests.
 */
export function isCompanyGiftCardsEnabled(source: EmployerModuleEnv = hostEnv): boolean {
  return isOn(source.VITE_COMPANY_GIFT_CARDS_ENABLED);
}

/**
 * EP-BE8-web dark switch — the company OCCASION PROGRAMS surface (standing birthday /
 * work-anniversary / new-hire gift-card programs the backend executor fulfils on a daily sweep).
 * DEFAULT OFF in every environment.
 *
 * Activation is a TWO-SIDED flip in one promotion round: set this build-time var
 * `VITE_COMPANY_OCCASION_PROGRAMS_ENABLED=true` for the env's admin build AND flip the backend
 * `COMPANY_OCCASION_PROGRAMS_ENABLED` env var for the same env together — the nine
 * `/admin/company/occasion-programs*` + `/admin/company/employees/*occasion-dates*` routes 404 while
 * the backend flag is off (it also serves as the Terraform `count` on the daily Cloud Scheduler job,
 * so a program authored against an OFF backend would never execute).
 *
 * ★ It gates occasion affordances on TWO differently-exposed surfaces, which is why it exists at all
 * rather than reusing a neighbouring switch:
 *   - the Programs area inside `/gift-cards`, itself already dark behind
 *     `isCompanyGiftCardsEnabled()` — INDEPENDENT of this one, and the two can legitimately skew
 *     (gift cards may go live first), so a surface hosted there must treat an occasion-route 404 as
 *     "not available in this environment" rather than as an error;
 *   - the occasion-date affordances on `/employees`, which is **LIVE**. Nothing else gates that
 *     page, so an ungated import button or date dialog would render to real admins as a visible
 *     control that 404s on click.
 *
 * Read from the env the host handed over; `source` is injectable for tests.
 */
export function isCompanyOccasionProgramsEnabled(source: EmployerModuleEnv = hostEnv): boolean {
  return isOn(source.VITE_COMPANY_OCCASION_PROGRAMS_ENABLED);
}

/**
 * EP-WEB10 dark switch — the company STEPS COMPETITIONS area (a company running a walking competition
 * whose prize is a company-funded donation to the winner's charity, via
 * `/admin/company/steps-competitions*`). DEFAULT OFF in every environment: while off the whole area is
 * UNREACHABLE (no sidebar entry renders, and the `/competitions*` routes redirect to `/dashboard`), so
 * an admin never sees a surface whose endpoints 404 at the backend.
 *
 * Activation is a TWO-SIDED flip: set this build-time var `VITE_COMPANY_STEPS_COMPETITIONS_ENABLED=true`
 * for the env's admin build AND have the backend `STEPS_COMPETITIONS_ENABLED` env var on for the same
 * env — every admin route is behind `_require_steps_competitions_enabled()`, so a visible area against
 * an OFF backend would fail on every read. Read from the env the host handed over; `source` is
 * injectable for tests.
 */
export function isCompanyStepsCompetitionsEnabled(source: EmployerModuleEnv = hostEnv): boolean {
  return isOn(source.VITE_COMPANY_STEPS_COMPETITIONS_ENABLED);
}

/**
 * EP-SUITE2 dark switch — domain-verified JOIN-OR-CREATE at employer-portal sign-up. DEFAULT OFF in
 * every environment. While off the sign-up flow is BYTE-IDENTICAL to today: no `resolveDomain()` call
 * is made, and a duplicate-work-email-domain create still throws (rolling the Firebase user back) as
 * it always has. While on, after a caller's work email is verified the flow resolves the onboarding
 * branch (`POST /admin/company/resolve`) and can pivot to a REQUEST-ACCESS screen (join an existing
 * company) instead of creating a duplicate — and the Owner "Join requests" inbox renders.
 *
 * This is the web pair of the backend `COMPANY_JOIN_OR_CREATE_ENABLED` env flag: while the backend
 * flag is off, `/admin/company/resolve` + `/admin/company/join-requests*` + `/admin/company/products`
 * 404, so activation is a TWO-SIDED flip in one promotion round — set this build-time var
 * `VITE_COMPANY_JOIN_OR_CREATE_ENABLED=true` for the env's admin build AND flip the backend
 * `COMPANY_JOIN_OR_CREATE_ENABLED` env var for the same env together. Read from the env the host
 * handed over; `source` is injectable for tests.
 */
export function isCompanyJoinOrCreateEnabled(source: EmployerModuleEnv = hostEnv): boolean {
  return isOn(source.VITE_COMPANY_JOIN_OR_CREATE_ENABLED);
}
